import { useState, useEffect, useMemo, useCallback } from 'react';
import * as api from '@/services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faChevronDown,
  faChevronRight,
  faCode,
  faCodeCommit,
  faFile,
  faFire,
  faMoon,
  faSpinner,
  faSun,
  faTrophy,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

interface Commit {
  hash: string;
  author: string;
  message: string;
  date: string;
  additions: number;
  deletions: number;
  branch: string;
  isMerge?: boolean;
}

interface AuthorStats {
  name: string;
  commits: number;
  additions: number;
  deletions: number;
  activeDays: number;
}

interface RepoStats {
  totalCommits: number;
  totalAuthors: number;
  totalAdditions: number;
  totalDeletions: number;
  authors: AuthorStats[];
}

interface RepoConfig {
  type: 'local' | 'bitbucket' | 'github' | 'gitlab';
  path?: string;
  url?: string;
  name?: string;
  username?: string;
  appPassword?: string;
}

interface LiveBoardProps {
  stats: RepoStats | null;
  commits: Commit[];
  repoName: string;
  onClose: () => void;
  isLive?: boolean;
  isRefreshing?: boolean;
  lastRefresh?: Date;
  repoConfig?: RepoConfig;
}

export function LiveBoard({ stats, commits, repoName, onClose, isLive = true, isRefreshing = false, lastRefresh: _lastRefresh, repoConfig }: LiveBoardProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [highlightedCommit, setHighlightedCommit] = useState<number>(0);
  const [isDark, setIsDark] = useState(true);
  const [hideMerges, setHideMerges] = useState(false);

  // Diff viewer state
  const [selectedCommit, setSelectedCommit] = useState<Commit | null>(null);
  const [diff, setDiff] = useState<string>('');
  const [diffLoading, setDiffLoading] = useState(false);
  const [expandedDiffFiles, setExpandedDiffFiles] = useState<Set<number>>(new Set());

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Cycle through highlighted commits
  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightedCommit(prev => (prev + 1) % Math.min(commits.length, 6));
    }, 4000);
    return () => clearInterval(interval);
  }, [commits.length]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedCommit) {
          setSelectedCommit(null);
          setDiff('');
        } else {
          onClose();
        }
      }
      if (e.key === 't' || e.key === 'T') setIsDark(d => !d);
      if (e.key === 'm' || e.key === 'M') setHideMerges(h => !h);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, selectedCommit]);

  // Fetch diff for a commit
  const fetchDiff = useCallback(async (commit: Commit) => {
    if (!repoConfig) return;
    setDiffLoading(true);
    setDiff('');
    setExpandedDiffFiles(new Set());
    try {
      const config = { ...repoConfig, hash: commit.hash };
      const data = await api.getCommitDiff(config as any, commit.hash);
      setDiff(data.diff || 'No diff available');
    } catch {
      setDiff('Error loading diff');
    } finally {
      setDiffLoading(false);
    }
  }, [repoConfig]);

  const handleCommitClick = useCallback((commit: Commit) => {
    setSelectedCommit(commit);
    fetchDiff(commit);
  }, [fetchDiff]);

  const parsedDiffFiles = useMemo(() => {
    if (!diff) return [];
    const files: { filename: string; additions: number; deletions: number; lines: string[] }[] = [];
    const rawLines = diff.split('\n');
    let current: typeof files[0] | null = null;

    for (const line of rawLines) {
      if (line.startsWith('diff --git')) {
        const match = line.match(/diff --git a\/(.*?) b\/(.*)/);
        const filename = match ? match[2] : line.replace('diff --git ', '');
        current = { filename, additions: 0, deletions: 0, lines: [] };
        files.push(current);
        current.lines.push(line);
      } else if (current) {
        current.lines.push(line);
        if (line.startsWith('+') && !line.startsWith('+++')) {
          current.additions++;
        } else if (line.startsWith('-') && !line.startsWith('---')) {
          current.deletions++;
        }
      }
    }
    return files;
  }, [diff]);

  const toggleDiffFile = useCallback((idx: number) => {
    setExpandedDiffFiles(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const expandAllDiffFiles = useCallback(() => {
    setExpandedDiffFiles(new Set(parsedDiffFiles.map((_, i) => i)));
  }, [parsedDiffFiles]);

  const collapseAllDiffFiles = useCallback(() => {
    setExpandedDiffFiles(new Set());
  }, []);

  // Sort commits by date (newest first), optionally filter out merges
  const sortedCommits = useMemo(() => {
    let filtered = [...commits];
    if (hideMerges) {
      filtered = filtered.filter(c => !c.isMerge);
    }
    return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [commits, hideMerges]);

  if (!stats) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center z-50 ${isDark ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`} style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
        <div className="text-2xl">No data available. Analyze a repository first.</div>
        <button onClick={onClose} className="absolute top-8 right-8 opacity-50 hover:opacity-100">
          <FontAwesomeIcon icon={faXmark} className="h-8 w-8" />
        </button>
      </div>
    );
  }

  const sortedAuthors = [...stats.authors].sort((a, b) => b.commits - a.commits);
  const topAuthors = sortedAuthors.slice(0, 8);
  const recentCommits = sortedCommits.slice(0, 10);
  const maxCommits = topAuthors[0]?.commits || 1;

  // Theme classes
  const theme = {
    bg: isDark ? 'bg-slate-950' : 'bg-gray-50',
    bgGradient: isDark 
      ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' 
      : 'bg-gradient-to-br from-gray-50 via-white to-gray-100',
    text: isDark ? 'text-white' : 'text-gray-900',
    textMuted: isDark ? 'text-white/40' : 'text-gray-500',
    textSubtle: isDark ? 'text-white/30' : 'text-gray-400',
    card: isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200 shadow-sm',
    cardHover: isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-50',
    badge: isDark ? 'bg-white/5' : 'bg-gray-100',
  };

  return (
    <div className={`fixed inset-0 z-50 overflow-hidden font-sans ${theme.bg}`} style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
      {/* Gradient background */}
      <div className={`absolute inset-0 ${theme.bgGradient}`} />
      
      {/* Subtle grid pattern */}
      <div 
        className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.5]'}`}
        style={{
          backgroundImage: isDark 
            ? `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`
            : `linear-gradient(rgba(0,0,0,.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.03) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      {/* Main content */}
      <div className="relative h-full flex flex-col p-6">
        {/* Header - Compact */}
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
              isDark 
                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-500/30' 
                : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-500/20'
            }`}>
              <FontAwesomeIcon icon={faCode} className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${theme.text}`}>{repoName}</h1>
              <p className={`text-xs ${theme.textMuted}`}>Activity Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Hide merges indicator */}
            {hideMerges && (
              <button
                onClick={() => setHideMerges(false)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  isDark ? 'bg-orange-500/20' : 'bg-orange-100'
                }`}
                title="Click or press M to show merges"
              >
                <span className={`text-xs font-medium ${isDark ? 'text-orange-400' : 'text-orange-700'}`}>
                  Merges Hidden
                </span>
              </button>
            )}
            
            {/* Live indicator */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              isLive 
                ? (isDark ? 'bg-green-500/20' : 'bg-green-100') 
                : (isDark ? 'bg-gray-500/20' : 'bg-gray-100')
            }`}>
              <div className={`relative flex h-2.5 w-2.5`}>
                {isLive && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                )}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                  isLive ? 'bg-green-500' : 'bg-gray-400'
                }`}></span>
              </div>
              <span className={`text-xs font-medium ${
                isLive 
                  ? (isDark ? 'text-green-400' : 'text-green-700')
                  : (isDark ? 'text-gray-400' : 'text-gray-600')
              }`}>
                {isRefreshing ? 'Updating...' : isLive ? 'LIVE' : 'PAUSED'}
              </span>
            </div>
            
            {/* Time */}
            <div className="text-right">
              <div className={`text-3xl font-light tracking-tight ${theme.text}`} style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className={`text-xs ${theme.textMuted}`}>
                {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
            </div>
            
            {/* Theme toggle */}
            <button 
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}
              title="Toggle theme (T)"
            >
              {isDark ? <FontAwesomeIcon icon={faSun} className="h-5 w-5 text-amber-400" /> : <FontAwesomeIcon icon={faMoon} className="h-5 w-5 text-slate-600" />}
            </button>
            
            {/* Close button */}
            <button 
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}
              title="Exit TV Mode (Esc)"
            >
              <FontAwesomeIcon icon={faXmark} className={`h-5 w-5 ${isDark ? 'text-white/80' : 'text-gray-600'}`} />
            </button>
          </div>
        </header>

        {/* Stats row - More compact */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <StatCard value={stats.totalCommits} label="Commits" icon={<FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4" />} color="indigo" isDark={isDark} />
          <StatCard value={stats.totalAuthors} label="Contributors" icon={<FontAwesomeIcon icon={faUsers} className="h-4 w-4" />} color="violet" isDark={isDark} />
          <StatCard value={`+${formatNumber(stats.totalAdditions)}`} label="Additions" icon={<FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4" />} color="emerald" isDark={isDark} />
          <StatCard value={`-${formatNumber(stats.totalDeletions)}`} label="Deletions" icon={<FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4 rotate-180" />} color="rose" isDark={isDark} />
        </div>

        {/* Main grid - Better space utilization */}
        <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
          {/* Leaderboard - Start at top */}
          <div className={`col-span-4 rounded-xl border p-4 flex flex-col ${theme.card}`}>
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faTrophy} className="h-4 w-4 text-amber-500" />
              <h2 className={`text-sm font-semibold ${theme.text}`}>Top Contributors</h2>
            </div>
            
            {/* Start at top, not center */}
            <div className="flex flex-col gap-2">
              {topAuthors.map((author, index) => (
                <div
                  key={author.name}
                  className={`
                    rounded-lg p-3 transition-all duration-300
                    ${index === 0 ? (isDark ? 'bg-amber-500/10 border-l-2 border-amber-400' : 'bg-amber-50 border-l-2 border-amber-400') : ''}
                    ${index === 1 ? (isDark ? 'bg-slate-400/10 border-l-2 border-slate-400' : 'bg-gray-100 border-l-2 border-gray-400') : ''}
                    ${index === 2 ? (isDark ? 'bg-orange-500/10 border-l-2 border-orange-500' : 'bg-orange-50 border-l-2 border-orange-400') : ''}
                    ${index > 2 ? (isDark ? 'bg-white/[0.02] border-l-2 border-white/10' : 'bg-gray-50 border-l-2 border-gray-200') : ''}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`
                      w-6 h-6 rounded flex items-center justify-center text-xs font-bold
                      ${index === 0 ? 'bg-amber-400/20 text-amber-500' : ''}
                      ${index === 1 ? (isDark ? 'bg-slate-400/20 text-slate-400' : 'bg-gray-200 text-gray-600') : ''}
                      ${index === 2 ? 'bg-orange-500/20 text-orange-500' : ''}
                      ${index > 2 ? (isDark ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-500') : ''}
                    `}>
                      {index + 1}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm font-medium truncate ${theme.text}`}>{author.name}</span>
                        {author.commits >= 50 && <FontAwesomeIcon icon={faFire} className="h-3 w-3 text-orange-400 flex-shrink-0" />}
                      </div>
                      <div className={`text-xs ${theme.textMuted}`}>{author.activeDays}d active</div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`text-lg font-semibold ${theme.text}`}>{author.commits}</div>
                    </div>
                  </div>
                  
                  <div className={`mt-2 h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-200'}`}>
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        index === 0 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                        index === 1 ? 'bg-gradient-to-r from-slate-400 to-slate-500' :
                        index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-500' :
                        isDark ? 'bg-gradient-to-r from-white/20 to-white/30' : 'bg-gradient-to-r from-gray-300 to-gray-400'
                      }`}
                      style={{ width: `${(author.commits / maxCommits) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Commits with diff snippets */}
          <div className={`col-span-8 rounded-xl border p-4 flex flex-col ${theme.card}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4 text-indigo-500" />
                <h2 className={`text-sm font-semibold ${theme.text}`}>Recent Activity</h2>
              </div>
              <span className={`text-xs ${theme.textSubtle}`}>Newest first</span>
            </div>
            
            <div className="flex-1 flex flex-col gap-1.5 overflow-y-auto">
              {recentCommits.map((commit, index) => (
                <div
                  key={commit.hash}
                  onClick={() => repoConfig && handleCommitClick(commit)}
                  className={`
                    rounded-lg p-2.5 transition-all duration-500 ${repoConfig ? 'cursor-pointer' : ''}
                    ${selectedCommit?.hash === commit.hash
                      ? (isDark ? 'bg-indigo-500/15 border border-indigo-500/30' : 'bg-indigo-100 border border-indigo-300')
                      : highlightedCommit === index 
                        ? (isDark ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200')
                        : (isDark ? 'bg-white/[0.02] border border-transparent' : 'bg-gray-50 border border-transparent')
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <code className={`text-xs font-mono px-1.5 py-0.5 rounded flex-shrink-0 ${
                      isDark ? 'text-indigo-400 bg-indigo-500/10' : 'text-indigo-600 bg-indigo-100'
                    }`}>
                      {commit.hash.substring(0, 7)}
                    </code>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${theme.text}`} style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {commit.message}
                      </p>
                      <div className={`flex items-center gap-2 mt-1.5 text-xs ${theme.textMuted}`}>
                        <span className="font-medium">{commit.author}</span>
                        <span>•</span>
                        <span className={isDark ? 'text-emerald-400/70' : 'text-emerald-600'}>+{commit.additions}</span>
                        <span className={isDark ? 'text-rose-400/70' : 'text-rose-600'}>-{commit.deletions}</span>
                        <span>•</span>
                        <span>{getTimeAgo(commit.date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Diff Viewer Panel — slides in from the right */}
        {selectedCommit && (
          <div className={`fixed right-0 top-0 h-full w-[55%] max-w-3xl z-[60] flex flex-col shadow-2xl border-l transition-transform ${
            isDark ? 'bg-slate-950 border-white/10' : 'bg-white border-gray-200'
          }`}>
            {/* Diff header */}
            <div className={`flex items-center justify-between p-4 border-b flex-shrink-0 ${
              isDark ? 'border-white/10' : 'border-gray-200'
            }`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <code className={`text-xs font-mono ${isDark ? 'text-indigo-400 bg-indigo-500/10' : 'text-indigo-600 bg-indigo-100'} px-1.5 py-0.5 rounded`}>
                    {selectedCommit.hash.substring(0, 7)}
                  </code>
                </div>
                <p className={`text-sm font-medium truncate ${theme.text}`}>
                  {selectedCommit.message}
                </p>
                <div className={`flex items-center gap-3 mt-1 text-xs ${theme.textMuted}`}>
                  <span className="font-medium">{selectedCommit.author}</span>
                  <span>{new Date(selectedCommit.date).toLocaleString()}</span>
                  <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>+{selectedCommit.additions}</span>
                  <span className={isDark ? 'text-rose-400' : 'text-rose-600'}>-{selectedCommit.deletions}</span>
                </div>
              </div>
              <button
                onClick={() => { setSelectedCommit(null); setDiff(''); }}
                className={`p-2 rounded-lg ml-3 flex-shrink-0 transition-colors ${
                  isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                }`}
              >
                <FontAwesomeIcon icon={faXmark} className={`h-5 w-5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
              </button>
            </div>

            {/* Diff content — collapsible per-file */}
            <div className="flex-1 overflow-y-auto overflow-x-auto p-0">
              {diffLoading ? (
                <div className="flex items-center justify-center h-full gap-2">
                  <FontAwesomeIcon icon={faSpinner} className={`h-5 w-5 animate-spin ${theme.textMuted}`} />
                  <span className={`text-sm ${theme.textMuted}`}>Loading diff...</span>
                </div>
              ) : parsedDiffFiles.length > 0 ? (
                <div className="p-2 space-y-1">
                  {/* File summary bar */}
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg mb-2 ${
                    isDark ? 'bg-white/[0.03]' : 'bg-gray-50'
                  }`}>
                    <span className={`text-xs font-medium ${theme.textMuted}`}>
                      {parsedDiffFiles.length} file{parsedDiffFiles.length !== 1 ? 's' : ''} changed
                      <span className={`ml-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        +{parsedDiffFiles.reduce((s, f) => s + f.additions, 0)}
                      </span>
                      <span className={`ml-1 ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                        -{parsedDiffFiles.reduce((s, f) => s + f.deletions, 0)}
                      </span>
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={expandAllDiffFiles}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-200 text-gray-500'
                        }`}
                      >
                        Expand all
                      </button>
                      <button
                        onClick={collapseAllDiffFiles}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-200 text-gray-500'
                        }`}
                      >
                        Collapse all
                      </button>
                    </div>
                  </div>

                  {parsedDiffFiles.map((file, fileIdx) => {
                    const isExpanded = expandedDiffFiles.has(fileIdx);
                    const totalChanges = file.additions + file.deletions;
                    const addPct = totalChanges > 0 ? Math.round((file.additions / totalChanges) * 100) : 0;
                    return (
                      <div key={fileIdx} className={`rounded-lg border overflow-hidden ${
                        isDark ? 'border-white/10' : 'border-gray-200'
                      }`}>
                        {/* File header */}
                        <button
                          onClick={() => toggleDiffFile(fileIdx)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                            isDark
                              ? 'bg-white/[0.03] hover:bg-white/[0.06]'
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          {isExpanded
                            ? <FontAwesomeIcon icon={faChevronDown} className={`h-3.5 w-3.5 flex-shrink-0 ${theme.textMuted}`} />
                            : <FontAwesomeIcon icon={faChevronRight} className={`h-3.5 w-3.5 flex-shrink-0 ${theme.textMuted}`} />
                          }
                          <FontAwesomeIcon icon={faFile} className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                          <span className={`text-xs font-mono truncate flex-1 ${theme.text}`}>
                            {file.filename}
                          </span>
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                            <span className={`text-xs font-mono font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              +{file.additions}
                            </span>
                            <span className={`text-xs font-mono font-medium ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                              -{file.deletions}
                            </span>
                            <div className="flex h-2 w-12 rounded-full overflow-hidden bg-gray-700/20 ml-1">
                              <div className="bg-emerald-500" style={{ width: `${addPct}%` }} />
                              <div className="bg-rose-500" style={{ width: `${100 - addPct}%` }} />
                            </div>
                          </div>
                        </button>

                        {/* File diff lines */}
                        {isExpanded && (
                          <div className="overflow-x-auto">
                            {file.lines.map((line, lineIdx) => {
                              let lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${isDark ? 'text-white/80' : 'text-gray-800'}`;
                              if (line.startsWith('+') && !line.startsWith('+++')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${
                                  isDark ? 'bg-green-500/10 text-green-300' : 'bg-green-100 text-green-800'
                                }`;
                              } else if (line.startsWith('-') && !line.startsWith('---')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${
                                  isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-100 text-red-800'
                                }`;
                              } else if (line.startsWith('@@')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${
                                  isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-100 text-blue-800'
                                }`;
                              } else if (line.startsWith('diff --git') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++')) {
                                lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 font-semibold ${
                                  isDark ? 'text-white/50 bg-white/[0.02]' : 'text-gray-500 bg-gray-50'
                                }`;
                              }
                              return <div key={lineIdx} className={lineClass}>{line || ' '}</div>;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4">
                  <pre className={`font-mono text-xs whitespace-pre-wrap ${theme.textMuted}`}>{diff}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-3 text-center">
          <span className={`text-xs ${theme.textSubtle}`}>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>ESC</kbd> {selectedCommit ? 'close diff' : 'exit'}
            <span className="mx-2">•</span>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>T</kbd> theme
            <span className="mx-2">•</span>
            <kbd className={`px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-200 text-gray-500'}`}>M</kbd> {hideMerges ? 'show' : 'hide'} merges
            {repoConfig && (
              <>
                <span className="mx-2">•</span>
                <span>Click commit to view diff</span>
              </>
            )}
          </span>
        </footer>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, isDark }: { 
  icon: React.ReactNode; 
  label: string; 
  value: string | number;
  color: 'indigo' | 'violet' | 'emerald' | 'rose';
  isDark: boolean;
}) {
  const darkColors = {
    indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
    violet: 'bg-violet-500/10 border-violet-500/20 text-violet-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    rose: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  };
  
  const lightColors = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-600',
    violet: 'bg-violet-50 border-violet-200 text-violet-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
    rose: 'bg-rose-50 border-rose-200 text-rose-600',
  };

  const colors = isDark ? darkColors : lightColors;

  return (
    <div className={`${colors[color]} border rounded-xl p-4`}>
      <div className="flex items-center gap-3">
        <div className="opacity-60">{icon}</div>
        <div>
          <div className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</div>
          <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{label}</div>
        </div>
      </div>
    </div>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

function getTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
